/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import * as React from 'react';
import { Component } from 'react';
import * as ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import Modal from 'react-modal';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
// import ReactGA from 'react-ga4';

// Use consistent styling
import 'sanitize.css/sanitize.css';

// api layer init
import { setResponseInterceptor } from './utils/api';

// Import root app
import { App } from 'app';

import { configureAppStore } from 'store/configureStore';

import reportWebVitals from 'reportWebVitals';

// Initialize languages
import './locales/i18n';

// Initialize Sentry
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [
    new BrowserTracing(),
  ],
  tracesSampleRate: 1.0,
  environment: process.env.NODE_ENV,
});

// Monitor for long tasks (UI freezes)
if ('PerformanceObserver' in window) {
  const observer = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry) => {
      if (entry.duration > 16) { // Tasks longer than 16ms (1 frame)
        Sentry.addBreadcrumb({
          message: `Long task detected: ${entry.duration}ms`,
          category: 'performance',
          level: entry.duration > 100 ? 'warning' : 'info',
          data: {
            duration: entry.duration,
            startTime: entry.startTime,
            url: window.location.pathname,
          },
        });
        
        if (entry.duration > 100) { // Report tasks over 100ms
          Sentry.captureMessage(`UI freeze detected: ${entry.duration}ms on ${window.location.pathname}`, 'warning');
        }
      }
    });
  });
  
  try {
    observer.observe({ entryTypes: ['longtask'] });
  } catch (e) {
    // Long task API not supported
  }
}

// Monitor for infinite loops and unresponsive behavior
let lastHeartbeat = Date.now();
let heartbeatCount = 0;

setInterval(() => {
  const now = Date.now();
  const timeSinceLastBeat = now - lastHeartbeat;
  
  if (timeSinceLastBeat > 5000) { // No heartbeat for 5 seconds
    Sentry.captureMessage(`App unresponsive for ${timeSinceLastBeat}ms on ${window.location.pathname}`, 'error');
  }
  
  lastHeartbeat = now;
  heartbeatCount++;
}, 1000);

// Track route changes for context
let currentPath = window.location.pathname;
setInterval(() => {
  if (window.location.pathname !== currentPath) {
    currentPath = window.location.pathname;
    Sentry.addBreadcrumb({
      message: `Route changed to ${currentPath}`,
      category: 'navigation',
      level: 'info',
    });
  }
}, 100);

// Send test event to verify Sentry is working (development only)
if (process.env.NODE_ENV === 'development') {
  setTimeout(() => {
    Sentry.captureMessage('Sentry test event - React app loaded successfully', 'info');
  }, 2000);
}

// Global error handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  Sentry.captureException(event.reason);
});

// Monitor memory usage
if ('memory' in performance) {
  setInterval(() => {
    const memory = (performance as any).memory;
    if (memory.usedJSHeapSize > memory.jsHeapSizeLimit * 0.9) {
      Sentry.captureMessage(`High memory usage: ${Math.round(memory.usedJSHeapSize / 1024 / 1024)}MB`, 'warning');
    }
  }, 10000);
}

// Fix react-modal accessibility warning
Modal.setAppElement('#root');

const store = configureAppStore();
const MOUNT_NODE = document.getElementById('root') as HTMLElement;
var route = window.location.pathname + window.location.search;

ReactDOM.render(
  <Provider store={store}>
    <HelmetProvider>
      <React.StrictMode>
        <App />
      </React.StrictMode>
    </HelmetProvider>
  </Provider>,
  MOUNT_NODE,
);

const { dispatch } = store; // direct access to redux store.
setResponseInterceptor(dispatch);

// Hot reloadable translation json files
if (module.hot) {
  module.hot.accept(['./locales/i18n'], () => {
    // No need to render the App again because i18next works with the hooks
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
